/**
 * Short campaign links: `chefty.app/go/<slug>` → the landing page with UTM tags.
 *
 * The slug is what gets pasted into a TikTok bio or a YouTube description; the API
 * counts the click and redirects to the tagged URL, which is what the analytics
 * reports group by. Both sides — the redirect and the admin's preview — build that
 * URL with `buildCampaignUrl`, so what an operator copies is what visitors get.
 *
 * Zod-free on purpose: this file is part of `@chefty/shared-types/constants`.
 */
/** Lowercase, digits and single dashes; 2–64 chars. Readable in a bio, unambiguous in a URL. */
export const CAMPAIGN_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export function isValidCampaignSlug(slug) {
    return slug.length >= 2 && slug.length <= 64 && CAMPAIGN_SLUG_PATTERN.test(slug);
}
/**
 * The destination a short link resolves to: the site, the stored path, and the UTM
 * tags appended to whatever query the path already had.
 *
 * `targetPath` is operator input, so it is forced back under `site`: an absolute or
 * protocol-relative value would otherwise make this an open redirect — a link on our
 * domain that sends people anywhere, which is how a domain's reputation gets spent.
 */
export function buildCampaignUrl(target, site) {
    const url = new URL(site);
    const path = new URL(target.targetPath, 'https://chefty.invalid');
    url.pathname = path.pathname;
    url.search = path.search;
    const params = url.searchParams;
    params.set('utm_source', target.utmSource);
    params.set('utm_medium', target.utmMedium);
    if (target.utmCampaign)
        params.set('utm_campaign', target.utmCampaign);
    if (target.utmContent)
        params.set('utm_content', target.utmContent);
    return url.toString();
}
/** `chefty.app/go/<slug>` — what goes into the profile. */
export function buildShortLinkUrl(slug, site) {
    return new URL(`/go/${slug}`, site).toString();
}
//# sourceMappingURL=campaigns.js.map